import { useEffect, useState } from 'react';
import * as ReactBootstrap from 'react-bootstrap';
import { toast } from 'react-toastify';
import getCameras from '../../web_api/methods/settings/cameras/getCameras';
import { camerasList, locationsList, technicalIncidentEventTypeList } from '../../web_api/models';

type TechnicalIncidentAddModalProps = {
  eventTypes: technicalIncidentEventTypeList;
  locations: locationsList;
  onHide: () => void;
  onSubmit: (payload: { cameraId: number; eventTypeId: number; image: File; message?: string }) => Promise<void>;
  show: boolean;
  submitting: boolean;
};

type FormState = {
  cameraId: number;
  eventTypeId: number;
  image: File | null;
  locationId: number;
  message: string;
};

const initialFormState: FormState = {
  cameraId: 0,
  eventTypeId: 0,
  image: null,
  locationId: 0,
  message: '',
};

const TechnicalIncidentAddModal: React.FC<TechnicalIncidentAddModalProps> = ({ eventTypes, locations, onHide, onSubmit, show, submitting }) => {
  const [availableCameras, setAvailableCameras] = useState<camerasList>([]);
  const [loadingCameras, setLoadingCameras] = useState(false);
  const [formState, setFormState] = useState<FormState>(initialFormState);
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    if (!show) {
      setAvailableCameras([]);
      setLoadingCameras(false);
      setFormState(initialFormState);
      setValidated(false);
    }
  }, [show]);

  useEffect(() => {
    const loadCameras = async () => {
      if (!show || !formState.locationId) {
        setAvailableCameras([]);
        setLoadingCameras(false);
        return;
      }

      try {
        setLoadingCameras(true);
        const cameras = await getCameras({ locationId: formState.locationId });
        setAvailableCameras(cameras);
      } catch (error: any) {
        console.log(error);
        setAvailableCameras([]);
        toast.error(`${error}`, {
          position: 'top-right',
          theme: 'colored',
        });
      } finally {
        setLoadingCameras(false);
      }
    };

    loadCameras();
  }, [formState.locationId, show]);

  const handleSubmit = async () => {
    setValidated(true);

    if (!formState.locationId || !formState.cameraId || !formState.eventTypeId || !formState.image) {
      return;
    }

    await onSubmit({
      cameraId: formState.cameraId,
      eventTypeId: formState.eventTypeId,
      image: formState.image,
      message: formState.message?.trim() || undefined,
    });
  };

  return (
    <ReactBootstrap.Modal
      centered
      size='lg'
      show={show}
      onHide={() => {
        if (!submitting) {
          onHide();
        }
      }}
      backdrop={submitting ? 'static' : true}
      keyboard={!submitting}
    >
      <ReactBootstrap.Modal.Header closeButton={!submitting}>
        <ReactBootstrap.Modal.Title>New Technical Incident</ReactBootstrap.Modal.Title>
      </ReactBootstrap.Modal.Header>
      <ReactBootstrap.Modal.Body>
        <ReactBootstrap.Form>
          <div className='row'>
            <div className='col-md-6 mb-5'>
              <ReactBootstrap.Form.Group controlId='technical-incident-location'>
                <ReactBootstrap.Form.Label>Location</ReactBootstrap.Form.Label>
                <ReactBootstrap.Form.Select
                  isInvalid={validated && !formState.locationId}
                  value={formState.locationId || ''}
                  onChange={(event) =>
                    setFormState((prevState) => ({
                      ...prevState,
                      cameraId: 0,
                      locationId: Number(event.target.value),
                    }))
                  }
                >
                  <option value=''>Select a location</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.id} - {location.name}
                    </option>
                  ))}
                </ReactBootstrap.Form.Select>
                <ReactBootstrap.Form.Control.Feedback type='invalid'>Location is required.</ReactBootstrap.Form.Control.Feedback>
              </ReactBootstrap.Form.Group>
            </div>
            <div className='col-md-6 mb-5'>
              <ReactBootstrap.Form.Group controlId='technical-incident-camera'>
                <ReactBootstrap.Form.Label>Camera</ReactBootstrap.Form.Label>
                <ReactBootstrap.Form.Select
                  disabled={!formState.locationId || loadingCameras || availableCameras.length === 0}
                  isInvalid={validated && !formState.cameraId}
                  value={formState.cameraId || ''}
                  onChange={(event) =>
                    setFormState((prevState) => ({
                      ...prevState,
                      cameraId: Number(event.target.value),
                    }))
                  }
                >
                  <option value=''>
                    {!formState.locationId ? 'Select a location first' : loadingCameras ? 'Loading cameras...' : 'Select a camera'}
                  </option>
                  {availableCameras.map((camera) => (
                    <option key={camera.id} value={camera.id}>
                      {camera.id} - {camera.name}
                    </option>
                  ))}
                </ReactBootstrap.Form.Select>
                <ReactBootstrap.Form.Control.Feedback type='invalid'>Camera is required.</ReactBootstrap.Form.Control.Feedback>
              </ReactBootstrap.Form.Group>
            </div>
          </div>
          <ReactBootstrap.Form.Group className='mb-5' controlId='technical-incident-event-type'>
            <ReactBootstrap.Form.Label>Event Type</ReactBootstrap.Form.Label>
            <ReactBootstrap.Form.Select
              isInvalid={validated && !formState.eventTypeId}
              value={formState.eventTypeId || ''}
              onChange={(event) =>
                setFormState((prevState) => ({
                  ...prevState,
                  eventTypeId: Number(event.target.value),
                }))
              }
            >
              <option value=''>Select an event type</option>
              {eventTypes.map((eventType) => (
                <option key={eventType.id} value={eventType.id}>
                  {eventType.name}
                </option>
              ))}
            </ReactBootstrap.Form.Select>
            <ReactBootstrap.Form.Control.Feedback type='invalid'>Event type is required.</ReactBootstrap.Form.Control.Feedback>
          </ReactBootstrap.Form.Group>
          <ReactBootstrap.Form.Group className='mb-5' controlId='technical-incident-message'>
            <ReactBootstrap.Form.Label>Message</ReactBootstrap.Form.Label>
            <ReactBootstrap.Form.Control
              as='textarea'
              placeholder='Add details about the technical incident'
              rows={4}
              value={formState.message || ''}
              onChange={(event) =>
                setFormState((prevState) => ({
                  ...prevState,
                  message: event.target.value,
                }))
              }
            />
          </ReactBootstrap.Form.Group>
          <ReactBootstrap.Form.Group controlId='technical-incident-image'>
            <ReactBootstrap.Form.Label>Image</ReactBootstrap.Form.Label>
            <ReactBootstrap.Form.Control
              accept='image/*'
              isInvalid={validated && !formState.image}
              type='file'
              onChange={(event) => {
                const files = (event.target as HTMLInputElement).files;

                setFormState((prevState) => ({
                  ...prevState,
                  image: files?.[0] || null,
                }));
              }}
            />
            <ReactBootstrap.Form.Control.Feedback type='invalid'>Image is required.</ReactBootstrap.Form.Control.Feedback>
          </ReactBootstrap.Form.Group>
        </ReactBootstrap.Form>
      </ReactBootstrap.Modal.Body>
      <ReactBootstrap.Modal.Footer>
        <ReactBootstrap.Button disabled={submitting} variant='secondary' onClick={onHide}>
          Close
        </ReactBootstrap.Button>
        <ReactBootstrap.Button disabled={submitting} variant='primary' onClick={handleSubmit}>
          {submitting ? <ReactBootstrap.Spinner animation='border' className='me-2' size='sm' /> : null}
          Create
        </ReactBootstrap.Button>
      </ReactBootstrap.Modal.Footer>
    </ReactBootstrap.Modal>
  );
};

export default TechnicalIncidentAddModal;
